import { native } from '@forest/native-services'
import { failureOf, withFailure } from '@forest/paywall-core'
import {
  createEffect,
  createSignal,
  ErrorBoundary,
  onMount,
  Show,
} from 'solid-js'
import { Transition } from 'solid-transition-group'
import { CountdownDialog } from './components/countdown-dialog'
import { Dialog } from './components/dialog'
import { EventHome } from './components/event-home'
import { IntroPage } from './components/intro-page'
import { eventStartDate } from './config'
import { isValidAppVersion } from './lib/params'
import { m } from './paraglide/messages'
import { useTimestamp } from './resources/timestamp'
import { showDialog } from './store/dialog'

const INTRO_VIEWED_KEY = 'birthday_2026_intro_viewed'

function hasViewedIntro() {
  try {
    return localStorage.getItem(INTRO_VIEWED_KEY) === '1'
  } catch {
    return false
  }
}

function markIntroViewed() {
  try {
    localStorage.setItem(INTRO_VIEWED_KEY, '1')
  } catch {
    // localStorage may be unavailable (private mode, WebView restrictions);
    // intro will simply replay next session.
  }
}

export function App() {
  const [introCompleted, setIntroCompleted] = createSignal(hasViewedIntro())
  const ts = useTimestamp()
  const campaignLive = () => ts().getTime() >= eventStartDate.getTime()

  onMount(() => {
    native.general.pageReady()
    native.general.logEvent({
      event_name: 'page_view',
      event_params: { campaign: 'birthday_2026' },
    })
    if (!isValidAppVersion) {
      showDialog({
        title: m.dialog_update_version_title(),
        description: m.dialog_update_version_description(),
        primaryButtonText: m.ok(),
        primaryAction: () => {
          native.general.close()
        },
      })
    }
  })

  const handleStart = () => {
    markIntroViewed()
    setIntroCompleted(true)
  }

  return (
    <>
      <ErrorBoundary
        fallback={(error) => {
          const failure = failureOf(error, 'load')
          createEffect(() => {
            if (error) {
              showDialog({
                title: m.dialog_unknown_error_title(),
                description:
                  withFailure(
                    m.dialog_unknown_error_content({
                      error_code: failure.code,
                    }),
                    failure,
                    { codeInDescription: true },
                  ) +
                  (import.meta.env.MODE === 'production'
                    ? ''
                    : `\n${String(error?.message ?? error)}`),
                primaryButtonText: m.ok(),
              })
            }
          })
          return (
            <div class="px-6 pt-24 text-white">
              Something went wrong: {failure.code}
            </div>
          )
        }}
      >
        <Show when={campaignLive()}>
          <EventHome started={introCompleted()} />
          <Transition exitActiveClass="pointer-events-none animate-out fade-out [animation-duration:600ms] ease-out">
            <Show when={!introCompleted()}>
              <div class="fixed inset-0 z-10">
                <IntroPage onStart={handleStart} />
              </div>
            </Show>
          </Transition>
        </Show>
        <CountdownDialog startDate={eventStartDate} />
      </ErrorBoundary>
      <Dialog />
    </>
  )
}
